import ControlActionModalContent from '../components/Modals/ControlActionModalContent/ControlActionModalContent';
import AggregateResultModalContent from '../components/Modals/AggregateResultModalContent/AggregateResultModalContent';
import PrintTemplateModalContent from '../components/Modals/PrintTemplateModalContent/PrintTemplateModalContent';
import {
  AggregateControlAction,
  ControlSelectionMode,
  DatasetControl,
} from '../types/types';
import { getButtonColorFromControlIcon } from '../utils/getButtonColorFromControlIcon';
import { isEmptyValue } from '../utils/managerUtils';
import { getControlLabelValue } from './useControlAction/utils/getControlLabelValue';
import { useModal } from './useModal';
import { AggregateResultItem } from '../components/Modals/AggregateResultModalContent/types';
import { ReportTemplateSummary } from '../pages/DatasetPrint/types';
import { useTranslate } from './context/useTranslate';

export const useControlActionModalContent = () => {
  const { open, close, isOpen, isLoading, modalProps, setIsLoading } =
    useModal();
  const { t } = useTranslate();

  const getDelimiter = (confirmMsgXDelimiter?: string): string => {
    switch (confirmMsgXDelimiter) {
      case 'newline':
        return '\n';
      case 'semicolon':
        return ';';
      default:
        return ',';
    }
  };

  const getLabelParam = (
    delimiter: string,
    dataset: any,
    rowsToUse: any[]
  ): string => {
    const labelColumnName =
      dataset?.labelItem ||
      dataset?.items?.find((item: any) => item.visible !== false)?.name;
    const labelColumn = dataset?.items?.find(
      (item: any) => item.name === labelColumnName
    );
    const labels = rowsToUse.map((row) =>
      getControlLabelValue(row, labelColumn, labelColumnName)
    );
    const label = labels.reduce(
      (prev: string, acc: string) =>
        prev === '' ? acc : `${prev}${delimiter} ${acc}`,
      ''
    );

    return label;
  };

  const getMessage = (dataset: any, control: any, rowsToUse: any[]): string => {
    const selectionMode: ControlSelectionMode =
      control?.selectionMode ?? 'dontcare'; // "dontcare" is the fallback selection mode
    const delimiter = getDelimiter(control?.confirmMsgXDelimiter);
    const labelVariable = getLabelParam(delimiter, dataset, rowsToUse);

    let message: string = '';

    const useConfirmMsg0 =
      !isEmptyValue(control?.confirmMsg0) && rowsToUse.length === 0;
    const useConfirmMsg1 =
      !isEmptyValue(control?.confirmMsg1) && rowsToUse.length === 1;
    const useConfirmMsgX =
      !isEmptyValue(control?.confirmMsgX) &&
      rowsToUse.length > 1 &&
      rowsToUse.length <= 5;
    const useConfirmMsgXMore =
      !isEmptyValue(control?.confirmMsgXMore) &&
      !isEmptyValue(control?.confirmMsgX) &&
      rowsToUse.length > 5;

    // When "dontcare" or "dontcare_useall" olways use confirmMsg0
    if (
      (selectionMode === 'dontcare' || selectionMode === 'dontcare_useall') &&
      !isEmptyValue(control?.confirmMsg0)
    ) {
      message = control.confirmMsg0;
      return message.replace('%s', labelVariable);
    }

    // When "any" use message by avaliability of messages and rows in selectedRows property
    if (selectionMode === 'any') {
      if (useConfirmMsg0) {
        return control?.confirmMsg0.replace('%s', labelVariable);
      }
      if (useConfirmMsg1) {
        return control?.confirmMsg1.replace('%s', labelVariable);
      }
      if (useConfirmMsgX) {
        return control?.confirmMsgX.replace('%s', labelVariable);
      }
      if (useConfirmMsgXMore) {
        return control?.confirmMsgX
          .replace('%s', control.confirmMsgXMore)
          .replace('%d', rowsToUse.length);
      }
    }

    // Whe "justone" use confirmMsg1 if avaliable. Apply also for  expandedRow.
    if (selectionMode === 'justone') {
      if (useConfirmMsg1) {
        return control?.confirmMsg1.replace('%s', labelVariable);
      }
    }

    // Will be transferred all selected records as list if selected one or more items
    if (selectionMode === 'oneormore') {
      if (useConfirmMsg1) {
        return control?.confirmMsg1.replace('%s', labelVariable);
      }
      if (useConfirmMsgX) {
        return control?.confirmMsgX.replace('%s', labelVariable);
      }
      if (useConfirmMsgXMore) {
        return control?.confirmMsgX
          .replace('%s', control.confirmMsgXMore)
          .replace('%d', rowsToUse.length);
      }
    }

    throw new Error(
      'Unable to generate message for selection mode: ' + selectionMode
    );
  };

  const openControlActionModal = (
    dataset: any,
    control: any,
    rowsToUse: any[],
    onConfirm: (() => Promise<void>) | (() => void)
  ) => {
    const modalType = getButtonColorFromControlIcon(control.icon);
    const modalContent = (
      <ControlActionModalContent
        text={getMessage(dataset, control, rowsToUse)}
        // If the modal type is danger, use the modal type, otherwise use default
        // Supports extensibility for other modal types in future if needed
        type={modalType === 'danger' ? modalType : 'default'}
        header={control.label}
        onConfirm={onConfirm}
        onCancel={close}
      />
    );
    open(modalContent);
  };

  const openAggregateResultModal = (
    control: DatasetControl,
    items: AggregateResultItem[],
    isForSelectedRows?: boolean
  ) => {
    const header = isForSelectedRows
      ? `${control.label} - ${t('aggregate.selectedRecordsTitle')}`
      : control.label;
    open(
      <AggregateResultModalContent
        header={header}
        items={items}
        onClose={close}
        aggregateItems={(control.action as AggregateControlAction).items}
      />,
      { isUnclosable: false }
    );
  };

  // Lists the reports available for a view; the designer entry is optional.
  const openPrintTemplateModal = (
    header: string,
    templates: ReportTemplateSummary[],
    onSelect: (templateId: string) => void,
    onOpenDesigner?: (templateId: string) => Promise<void>,
    onCreateTemplate?: () => Promise<void>,
    onDeactivateTemplate?: (templateId: string) => Promise<void>,
    onCopyTemplate?: (templateId: string) => Promise<void>,
    hasTemplateListError?: boolean
  ) => {
    open(
      <PrintTemplateModalContent
        header={header}
        templates={templates}
        onSelect={onSelect}
        onOpenDesigner={onOpenDesigner}
        onCreateTemplate={onCreateTemplate}
        onDeactivateTemplate={onDeactivateTemplate}
        onCopyTemplate={onCopyTemplate}
        hasTemplateListError={hasTemplateListError}
        onClose={close}
      />,
      { isUnclosable: false }
    );
  };

  return {
    open: openControlActionModal,
    openAggregateResultModal,
    openPrintTemplateModal,
    close,
    setModalLoading: setIsLoading,
    isModalOpen: isOpen || isLoading,
    modalProps,
  };
};
